import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ProgressSpinner } from 'primereact/progressspinner';
import { BlockUI } from 'primereact/blockui';

import InvoiceTable from './components/InvoiceTable';
import InvoiceDetailModal from './components/InvoiceDetailModal';
import HistoryPageModal from './components/HistoryPageModal';
import InvoiceApprovalFilter from './components/InvoiceFilter';
import ApprovalNoteDialog from './components/ApprovalNoteDialog';
import ProvisionPreviewDialog from './components/ProvisionPreviewDialog';
import { useInvoiceApproval } from './hooks/useInvoiceApproval';
import { useLanguage } from '../../../../contexts/LanguageContext';

const InvoiceApprovalPage = () => {
    const { th } = useLanguage();
    const toast = useRef(null);
    const [showHistoryModal, setShowHistoryModal] = useState(false);
    const [isProcessingApproval, setIsProcessingApproval] = useState(false);

    const [provisionDialogVisible, setProvisionDialogVisible] = useState(false);
    const [provisionDialogMode, setProvisionDialogMode] = useState('selected');

    // Approval note dialog state
    const [approvalDialogVisible, setApprovalDialogVisible] = useState(false);
    const [approvalDialogMode, setApprovalDialogMode] = useState('selected');

    const {
        invoiceData, selectedInvoices,
        isLoadingInvoices, isUpdating,
        onRowCheckboxChange, onSelectAllChange,
        handleApproval, handleApproveAll,
        formatCurrency, performSearch, handleResetFilters,
        detailModalVisible, invoiceDetails, isLoadingDetails,
        handleInfoClick, handleCloseDetailModal,
        totalRecords, lazyParams, handlePageChange,
        setStartDate, setEndDate,
        handlePreview, previewData, isLoadingPreview, previewError
    } = useInvoiceApproval();

    useEffect(() => { setIsProcessingApproval(isUpdating); }, [isUpdating]);

    const handleApplyFilters = useCallback((filterValues) => {
        const { startDate, endDate, dealer, warrantyType, domesticType } = filterValues;
        
        if (startDate) setStartDate(startDate);
        if (endDate) setEndDate(endDate);

        if (!startDate) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('invoiceApproval.messages.selectStartDate'), life: 3000 });
            return;
        }

        const dealerPayload = dealer || { isAllDealers: false, dealerIds: [] };
        if (!dealerPayload.isAllDealers && (!dealerPayload.dealerIds || dealerPayload.dealerIds.length === 0)) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('invoiceApproval.filters.selectDealer'), life: 3000 });
            return;
        }

        const warrTypePayload = warrantyType || { isAllWarrTypes: false, warrTypeIds: [] };
        if (!warrTypePayload.isAllWarrTypes && (!warrTypePayload.warrTypeIds || warrTypePayload.warrTypeIds.length === 0)) {
            toast.current?.show({ severity: 'error', summary: th('common.error'), detail: th('invoiceApproval.filters.selectWarrantyType'), life: 3000 });
            return;
        }

        performSearch(dealerPayload, warrTypePayload, domesticType ?? null, startDate, endDate);
    }, [performSearch, setStartDate, setEndDate, th]);

    const handleReset = useCallback(() => {
        handleResetFilters();
    }, [handleResetFilters]);

    // "Seçilenleri Onayla" → Provizyon önizleme aç
    const showApprovalConfirmation = async () => {
        if (selectedInvoices.length === 0) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('invoiceApproval.messages.selectRecordsToApprove'), life: 3000 });
            return;
        }
        setProvisionDialogMode('selected');
        setProvisionDialogVisible(true);
        await handlePreview(false);
    };

    // "Tümünü Onayla" → Provizyon önizleme aç
    const showApproveAllConfirmation = async () => {
        if (invoiceData.length === 0) {
            toast.current?.show({ severity: 'warn', summary: th('common.warning'), detail: th('invoiceApproval.messages.noRecordsToApprove'), life: 3000 });
            return;
        }
        setProvisionDialogMode('all');
        setProvisionDialogVisible(true);
        await handlePreview(true);
    };

    // Provizyon onaylandı → Not dialog'una geç
    const handleProvisionConfirm = () => {
        setProvisionDialogVisible(false);
        setApprovalDialogMode(provisionDialogMode);
        setApprovalDialogVisible(true);
    };

    const handleApprovalConfirm = async (note) => {
        setApprovalDialogVisible(false);

        let result;
        if (approvalDialogMode === 'all') {
            result = await handleApproveAll(note);
        } else {
            result = await handleApproval(null, note);
        }

        if (result) {
            toast.current?.show({
                severity: result.success ? 'success' : 'error',
                summary: result.success ? th('common.success') : th('common.error'),
                detail: result.message,
                life: 3000
            });
        }
    };

    const processingTemplate = (
        <div className="flex flex-column align-items-center justify-content-center">
            <ProgressSpinner style={{ width: '50px', height: '50px' }} strokeWidth="4" className="mb-3" />
            <div className="text-center text-white font-bold text-xl">{th('invoiceApproval.messages.processingApproval')}</div>
            <div className="text-center text-white mt-2">{th('invoiceApproval.messages.pleaseWait')}...</div>
        </div>
    );

    return (
        <div className="card relative">
            <Toast ref={toast} />

            <InvoiceDetailModal visible={detailModalVisible} onHide={handleCloseDetailModal} invoiceDetails={invoiceDetails} isLoading={isLoadingDetails} />
            <HistoryPageModal visible={showHistoryModal} onHide={() => setShowHistoryModal(false)} />

            {/* Provision Preview Dialog */}
            <ProvisionPreviewDialog
                visible={provisionDialogVisible}
                onHide={() => setProvisionDialogVisible(false)}
                onConfirm={handleProvisionConfirm}
                previewData={previewData}
                isLoading={isLoadingPreview}
                isError={previewError}
                isApproveAll={provisionDialogMode === 'all'}
            />

            {/* Approval Note Dialog */}
            <ApprovalNoteDialog
                visible={approvalDialogVisible}
                onHide={() => setApprovalDialogVisible(false)}
                onConfirm={handleApprovalConfirm}
                count={approvalDialogMode === 'all' ? totalRecords : selectedInvoices.length}
                isApproveAll={approvalDialogMode === 'all'}
            />

            <BlockUI blocked={isProcessingApproval} template={processingTemplate}>
                <h1 className="mt-0 mb-3">{th('invoiceApproval.pageTitle')}</h1>

                <InvoiceApprovalFilter
                    handleApplyFilters={handleApplyFilters}
                    handleResetFilters={handleReset}
                />

                <div className="flex justify-content-between align-items-center mb-3">
                    <div className="flex gap-2">
                        {invoiceData.length > 0 && (
                            <>
                                <Button label={th('invoiceApproval.buttons.approveSelected')} icon="pi pi-check" onClick={showApprovalConfirmation} disabled={selectedInvoices.length === 0} className="p-button-success" />
                                <Button label={th('invoiceApproval.buttons.approveAll')} icon="pi pi-check-circle" onClick={showApproveAllConfirmation} className="p-button-help" outlined />
                            </>
                        )}
                    </div>
                    <Button label={th('invoiceApproval.buttons.previousApprovals')} icon="pi pi-history" className="p-button-secondary" outlined onClick={() => setShowHistoryModal(true)} />
                </div>

                <InvoiceTable
                    invoiceData={invoiceData}
                    isLoadingInvoices={isLoadingInvoices}
                    onRowCheckboxChange={onRowCheckboxChange}
                    onSelectAllChange={onSelectAllChange}
                    formatCurrency={formatCurrency}
                    onInfoClick={handleInfoClick}
                    isProcessingApproval={isProcessingApproval}
                    totalRecords={totalRecords}
                    lazyParams={lazyParams}
                    onPageChange={handlePageChange}
                />

                {selectedInvoices.length > 0 && (
                    <div className="mt-3 p-3 bg-blue-50 border-round border-1 border-blue-200">
                        <p className="text-blue-800 m-0">
                            <i className="pi pi-info-circle mr-2"></i>
                            {th('invoiceApproval.messages.recordsSelected').replace('{count}', selectedInvoices.length)}
                        </p>
                    </div>
                )}
            </BlockUI>
        </div>
    );
};

export default InvoiceApprovalPage;
